/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {
  EnvironmentProviders,
  ErrorHandler,
  NgModule,
  Provider,
} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';

import {NgOptimizedImage} from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {AuthModule} from '@auth0/auth0-angular';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {IxModule} from '@siemens/ix-angular';
import {AgGridModule} from 'ag-grid-angular';
import {NgxEchartsDirective, NgxEchartsModule} from 'ngx-echarts';
import {NgxPermissionsModule} from 'ngx-permissions';
import {environment as env} from '../environments/environment';
import {CustomAuthHttpInterceptor} from '../services/custom-interceptor.service';
import {errorResponseInterceptor} from '../services/error-response.interceptor';
import {GlobalErrorHandlerService} from '../services/global-error-handler.service';
import {Auth0AuthenticationProviderService} from '../services/authentication/auth0/auth0-authentication-provider.service';
import {AUTHENTICATION_PROVIDER} from '../services/authentication/authentication-provider.token';
import {getAuthenticationProviderType} from '../services/authentication/authentication-provider-selection';
import {KeycloakAuthenticationProviderService} from '../services/authentication/keycloak/keycloak-authentication-provider.service';
import {provideKeycloakAuthentication} from '../services/authentication/keycloak/keycloak-provider.config';
import {DataProviderAssignmentDetailsComponent} from './components/battery/attribute-assignment/data-provider-assignment-details/data-provider-assignment-details.component';
import {DataProviderAssignmentListComponent} from './components/battery/attribute-assignment/data-provider-assignment-list/data-provider-assignment-list.component';
import {BatteryDashboardComponent} from './components/battery/battery-dashboard/battery-dashboard.component';
import {BatteryInteractionDifferencesComponent} from './components/battery/battery-interaction-differences/battery-interaction-differences.component';
import {BatteryInteractionHistoryComponent} from './components/battery/battery-interaction-history/battery-interaction-history.component';
import {BatteryPassEcosystemComponent} from './components/battery/battery-pass-ecosystem/battery-pass-ecosystem.component';
import {CreateBatteryPassComponent} from './components/battery/create-battery-pass/create-battery-pass.component';
import {EditBatteryPassComponent} from './components/battery/edit-battery-pass/edit-battery-pass.component';
import {ListBatteryPassesComponent} from './components/battery/list-battery-passes/list-battery-passes.component';
import {ListRemovedBatteryPassesComponent} from './components/battery/list-removed-battery-passes/list-removed-battery-passes.component';
import {ModalCreateBatteryPassComponent} from './components/battery/modal-create-battery-pass/modal-create-battery-pass.component';
import {PrivateBatteryPassViewerComponent} from './components/battery/private-battery-pass-viewer/private-battery-pass-viewer.component';
import {PublicBatteryPassViewerComponent} from './components/battery/public-battery-pass-viewer/public-battery-pass-viewer.component';
import {SearchBatteryPassComponent} from './components/battery/search-battery-pass/search-battery-pass.component';
import {EdcConfigComponent} from './components/edc-config/edc-config.component';
import {EmailSenderConfigComponent} from './components/email-sender-config/email-sender-config.component';
import {LoginComponent} from './components/login/login.component';
import {ListPassportComponent} from './components/dpp/list-passport/list-passport.component';
import {HandoverDocumentationComponent} from './components/dpp/handover-documentation/handover-documentation.component';
import {OrganizationsSettingsComponent} from './components/organizations-settings/organizations-settings.component';
import {BrandDomainSettingsComponent} from './components/brand-domain-settings/brand-domain-settings.component';
import {OverviewComponent} from './components/overview/overview.component';
import {EditUserComponent} from './components/user-management/edit-user/edit-user.component';
import {InviteUserComponent} from './components/user-management/invite-user/invite-user.component';
import {EditCellRendererComponent} from './components/user-management/list-user/edit-cell-renderer.component';
import {ListUserComponent} from './components/user-management/list-user/list-user.component';
import {StatusCellRendererComponent} from './components/user-management/list-user/status-cell-renderer.component';
import {UserSettingsComponent} from './components/user-settings/user-settings.component';
import {FeedbackFormComponent} from './shared/feedback-form/feedback-form.component';
import {RequestAccessFormComponent} from './shared/request-access-form/request-access-form.component';
import {FooterComponent} from './shared/footer/footer.component';
import {NavbarComponent} from './shared/navbar/navbar.component';
import {PassViewerComponent} from './shared/pass-viewer/pass-viewer.component';
import {TopicCardComponent} from './shared/topic-card/topic-card.component';
import {UserFormComponent} from './shared/user-form/user-form.component';
import {ErrorComponent} from './components/error/error.component';
import {FeatureFlagDirective} from '../directives/feature-flag.directive';
import {FeatureFlagImageDirective} from '../directives/feature-flag-image.directive';
import {PassportSearchComponent} from './components/dpp/list-passport/passport-search/passport-search.component';
import {LegalDisclaimerComponent} from './components/legal-disclaimer/legal-disclaimer.component';
import {DynamicFooterComponent} from './shared/dynamic-footer/dynamic-footer.component';
import {ModalImportBatteryDataComponent} from './components/battery/modal-import-battery-data/modal-import-battery-data.component';
import {MachineLoginComponent} from './components/machine-documentation/machine-login/machine-login.component';
import {QRCodeComponent} from 'angularx-qrcode';
import {ModalCreateChildrenComponent} from './components/battery/modal-create-children/modal-create-children.component';
import {SimpleModalComponent} from './shared/modals/simple-modal/simple-modal.component';
import {QrCodeModalComponent} from './shared/modals/qr-code-modal/qr-code-modal.component';
import {RemovePassportModalComponent} from './shared/modals/remove-passport-modal/remove-passport-modal.component';
import {IecQrCodeComponent} from './shared/iec-qr-code/iec-qr-code.component';
import {ImagePickerModalComponent} from './shared/modals/image-picker-modal/image-picker-modal.component';
import {CategoryWorkflowComponent} from './components/battery/edit-battery-pass/category-workflow/category-workflow.component';
import {AssignmentDetailsComponent} from './components/battery/edit-battery-pass/assignment-details/assignment-details.component';
import {FormBuilderComponent} from './components/battery/edit-battery-pass/form-builder/form-builder.component';
import {ViewerFieldComponent} from './shared/pass-viewer/components/viewer-field/viewer-field.component';
import {SideBarSearchComponent} from './components/battery/list-battery-passes/sidebar-search/sidebar-search.component';
import {PassListDetailsComponent} from './components/battery/list-battery-passes/pass-list-details/pass-list-details.component';
import {GlobalAlertComponent} from './shared/global-alert/global-alert.component';
import {AiChatbotComponentComponent} from './shared/ai-chatbot/ai-chatbot.component';
import {ModalCreateDppComponent} from './components/dpp/modal-create-dpp/modal-create-dpp.component';
import {ModalCreateSubmodelComponent} from './components/dpp/modal-create-submodel/modal-create-submodel.component';
import {ModalAddDocumentComponent} from './components/dpp/handover-documentation/modal-add-document/modal-add-document.component';
import {SubmodelViewerDispatcherComponent} from './components/dpp/submodel-viewer-dispatcher/submodel-viewer-dispatcher.component';
import {GenericSubmodelViewerComponent} from './components/dpp/generic-submodel-viewer/generic-submodel-viewer.component';
import {PublicViewerShellComponent} from './components/dpp/public-viewer-shell/public-viewer-shell.component';
import {DppDetailViewComponent} from './components/dpp/dpp-detail-view/dpp-detail-view.component';
import {PublicDppDetailViewComponent} from './components/dpp/public/public-dpp-detail-view/public-dpp-detail-view.component';
import {PublicGenericSubmodelViewerComponent} from './components/dpp/public/public-generic-submodel-viewer/public-generic-submodel-viewer.component';
import {PublicSubmodelViewerDispatcherComponent} from './components/dpp/public/public-submodel-viewer-dispatcher/public-submodel-viewer-dispatcher.component';
import {ListAccessRequestsComponent} from './components/user-management/list-access-requests/list-access-requests.component';
import {EditAccessRequestComponent} from './components/user-management/edit-access-request/edit-access-request.component';
import {VerifyEmailComponent} from './components/verify-email/verify-email.component';
import {ModalDeclineAccessRequestComponent} from './components/user-management/modal-decline-access-request/modal-decline-access-request.component';
import {ModalGrantAccessRequestComponent} from './components/user-management/modal-grant-access-request/modal-grant-access-request.component';
import {SchemaVersionLabelPipe} from './components/battery/schema-version-label.pipe';

const authenticationProviderType = getAuthenticationProviderType(
  env.authenticationProvider,
);
const authenticationProviders: Array<Provider | EnvironmentProviders> =
  authenticationProviderType === 'keycloak'
    ? [
        {
          provide: AUTHENTICATION_PROVIDER,
          useExisting: KeycloakAuthenticationProviderService,
        },
        provideKeycloakAuthentication(),
      ]
    : [
        {
          provide: AUTHENTICATION_PROVIDER,
          useExisting: Auth0AuthenticationProviderService,
        },
      ];

@NgModule({
  declarations: [
    AppComponent,
    OverviewComponent,
    ListBatteryPassesComponent,
    ListUserComponent,
    LoginComponent,
    UserFormComponent,
    UserSettingsComponent,
    CreateBatteryPassComponent,
    EditBatteryPassComponent,
    ListRemovedBatteryPassesComponent,
    BatteryPassEcosystemComponent,
    TopicCardComponent,
    PublicBatteryPassViewerComponent,
    NavbarComponent,
    FooterComponent,
    PassViewerComponent,
    PrivateBatteryPassViewerComponent,
    InviteUserComponent,
    EditUserComponent,
    BatteryDashboardComponent,
    BatteryInteractionHistoryComponent,
    FeedbackFormComponent,
    SearchBatteryPassComponent,
    RequestAccessFormComponent,
    BatteryInteractionDifferencesComponent,
    DataProviderAssignmentListComponent,
    DataProviderAssignmentDetailsComponent,
    ModalCreateBatteryPassComponent,
    OrganizationsSettingsComponent,
    EdcConfigComponent,
    EmailSenderConfigComponent,
    ListPassportComponent,
    HandoverDocumentationComponent,
    ErrorComponent,
    FeatureFlagDirective,
    FeatureFlagImageDirective,
    PassportSearchComponent,
    ModalImportBatteryDataComponent,
    MachineLoginComponent,
    ModalCreateChildrenComponent,
    SimpleModalComponent,
    QrCodeModalComponent,
    RemovePassportModalComponent,
    IecQrCodeComponent,
    ImagePickerModalComponent,
    LegalDisclaimerComponent,
    DynamicFooterComponent,
    SideBarSearchComponent,
    GlobalAlertComponent,
    ModalCreateDppComponent,
    ModalCreateSubmodelComponent,
    ModalAddDocumentComponent,
    SubmodelViewerDispatcherComponent,
    PublicSubmodelViewerDispatcherComponent,
    PublicViewerShellComponent,
    EditAccessRequestComponent,
    ModalDeclineAccessRequestComponent,
    ModalGrantAccessRequestComponent,
    ListAccessRequestsComponent,
    VerifyEmailComponent,
    SchemaVersionLabelPipe,
  ],
  bootstrap: [AppComponent],
  imports: [
    AgGridModule,
    EditCellRendererComponent,
    StatusCellRendererComponent,
    BrandDomainSettingsComponent,
    BrowserModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    AppRoutingModule,
    // Protected backend request matching is provider-neutral and is configured
    // by CustomAuthHttpInterceptor from environment.backend.
    AuthModule.forRoot({
      ...env.auth0,
    }),
    IxModule.forRoot(),
    NgOptimizedImage,
    NgxPermissionsModule.forRoot(),
    QRCodeComponent,
    FormsModule,
    ReactiveFormsModule,
    NgxEchartsDirective,
    NgxEchartsModule.forRoot({echarts: () => import('echarts')}),
    CategoryWorkflowComponent,
    AssignmentDetailsComponent,
    FormBuilderComponent,
    ViewerFieldComponent,
    PassListDetailsComponent,
    AiChatbotComponentComponent,
    GenericSubmodelViewerComponent,
    DppDetailViewComponent,
    PublicDppDetailViewComponent,
    PublicGenericSubmodelViewerComponent,
    TranslateModule,
    IxModule,
  ],
  providers: [
    ...authenticationProviders,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomAuthHttpInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandlerService,
    },
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([errorResponseInterceptor]),
    ),
  ],
})
export class AppModule {}

export function httpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}
